import { SummaryKpiFilters, CategoryDetailsFilters, ChartFilters } from 'models/request';
import { ChartData, Kpi } from 'models/response';
import services from 'services';
import utils from 'utils';
import { CHART_TYPE } from 'utils/constants';
import { colorPalette } from 'utils/styleConstants';
import { AreaWithGroupedKpis, GroupedTableKpiBlock, KpiBlock } from './interfaces';

interface KpiExtended extends Kpi {
  kpiTableIdGroup?: string;
}

export const getKpiSummary = async (params: SummaryKpiFilters) => {
  try {
    if (params.dateFrom && params.dateTo) {
      const { data } = await services.getSummary(params);
      return data;
    }
    return [];
  } catch (error) {
    console.error('Error fetching KPI summary:', error);
    return [];
  }
};

export const getGbsCompanies = async () => {
  try {
    const { data } = await services.getGbsCompanies();
    return data;
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const getDocumentTypes = async () => {
  try {
    const { data } = await services.getDocumentTypes();
    return data;
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const getUserCompanies = async (idUser: number, idProgram: number) => {
  try {
    const { data } = await services.getConfigurationsByCompanies(idUser, idProgram);
    return data;
  } catch (error) {
    console.error(error);
    return [];
  }
};

const organizeKpis = (kpis: KpiExtended[]) => {
  const result: KpiBlock[] = [];

  kpis.forEach((kpi) => {
    const groupId = kpi.kpiTableIdGroup;
    const hasChildMetrics = kpi.childMetrics && kpi.childMetrics.length > 0;

    if (groupId && hasChildMetrics) {
      const kpiGroup = result.find(
        (item) => item.type === 'grouped-table' && item.groupId === groupId,
      ) as GroupedTableKpiBlock;
      if (kpiGroup) {
        kpiGroup.kpis.push(kpi);
      } else {
        result.push({ type: 'grouped-table', groupId, kpis: [kpi] });
      }
      return;
    }

    if (hasChildMetrics) {
      result.push({ type: 'table', kpi });
      return;
    }

    result.push({ type: 'text', kpi });
  });

  return result;
};

export const getCategoryDetails = async (params: CategoryDetailsFilters) => {
  try {
    if (params.dateFrom && params.dateTo) {
      const { data } = await services.getCategoryDetails(params);

      data.forEach((category) => {
        category.areas.forEach((area) => {
          (area as AreaWithGroupedKpis).kpisGrouped = organizeKpis(area.kpis);
        });
      });

      return data;
    }
    return [];
  } catch (error) {
    console.error('Error fetching category details:', error);
    return [];
  }
};

export const getCharts = async (params: ChartFilters) => {
  try {
    if (params.dateFrom && params.dateTo) {
      const { line, pie, stackedBar } = CHART_TYPE;

      const { data: chartDataArray } = await services.getCharts(params);

      const addColor = (chartData: ChartData) => {
        const type = chartData.chartType.toLowerCase();

        if (type === line) {
          // generate colors for every dataset; exclude red, red is reserved for average line
          const colors = utils.colors.generateChartColors(chartData.series.length, colorPalette.graphics);

          return {
            ...chartData,
            series: chartData.series.map((dataset, index) => ({
              ...dataset,
              color: colors[index] || colorPalette.grey.grey7,
            })),
          };
        }

        if (type === stackedBar) {
          // Generate colors for every dataset
          const colors = utils.colors.generateChartColors(chartData.series.length, colorPalette.graphics);

          return {
            ...chartData,
            series: chartData.series.map((dataset, index) => ({
              ...dataset,
              color: colors[index] || colorPalette.grey.grey7,
            })),
          };
        }

        if (type === pie) {
          return {
            ...chartData,
            series: chartData.series.map((dataset) => {
              // Generate colors for every pair of data inside a series
              const colors = utils.colors.generateChartColors(dataset.data.length, colorPalette.graphics);

              const datasetTotal = dataset.data.reduce((acc, item) => {
                const num = Number(item.y);
                return !isNaN(num) ? acc + num : acc;
              }, 0);

              return {
                ...dataset,
                data: dataset.data.map((value, index) => {
                  const num = Number(value.y);
                  const percentage = !isNaN(num) && datasetTotal !== 0 ? (num / datasetTotal) * 100 : 0;
                  return {
                    ...value,
                    color: colors[index],
                    percentage: parseFloat(percentage.toFixed(2)),
                  };
                }),
              };
            }),
          };
        }

        return chartData;
      };

      const addFormattedY = (chartData: ChartData) => {
        return {
          ...chartData,
          series: chartData.series.map((dataset) => ({
            ...dataset,
            data: dataset.data.map((value, index) => ({
              ...value,
              formattedY: dataset.formattedData[index].y,
            })),
          })),
        };
      };

      const coloredChartDataArray = chartDataArray.map((chartData) => addColor(chartData));
      const formattedChartDataArray = coloredChartDataArray.map((chartData) => addFormattedY(chartData));

      return formattedChartDataArray;
    }

    return [];
  } catch (error) {
    console.error('Error fetching category details:', error);
    return [];
  }
};
